import { useState, useRef, useEffect, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Animation presets
export const popoverAnimations = {
  fadeScale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
  fadeSlideDown: {
    initial: { opacity: 0, y: -10, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -10, scale: 0.95 },
  },
  fadeSlideUp: {
    initial: { opacity: 0, y: 10, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 10, scale: 0.95 },
  },
};

type PopoverAnimation = keyof typeof popoverAnimations;
type PopoverAlign = 'left' | 'right' | 'center';

interface PopoverContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  close: () => void;
}

const PopoverContext = createContext<PopoverContextType | null>(null);

export function usePopover() {
  const context = useContext(PopoverContext);
  if (!context) {
    throw new Error('usePopover must be used within a Popover');
  }
  return context;
}

interface PopoverProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Popover({ children, open, onOpenChange }: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setIsOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const close = () => setIsOpen(false);

  return (
    <PopoverContext.Provider value={{ isOpen, setIsOpen, close }}>
      <div className="relative">{children}</div>
    </PopoverContext.Provider>
  );
}

interface PopoverTriggerProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

export function PopoverTrigger({ children, className = '', asChild }: PopoverTriggerProps) {
  const { isOpen, setIsOpen } = usePopover();

  if (asChild) {
    // Clone the child and add onClick
    return (
      <span onClick={() => setIsOpen(!isOpen)} className={className}>
        {children}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className={className}
    >
      {children}
    </button>
  );
}

interface PopoverContentProps {
  children: ReactNode;
  className?: string;
  align?: PopoverAlign;
  side?: 'top' | 'bottom';
  sideOffset?: number;
  animation?: PopoverAnimation;
  closeOnClickOutside?: boolean;
}

export function PopoverContent({
  children,
  className = '',
  align = 'right',
  side = 'bottom',
  sideOffset = 8,
  animation = 'fadeSlideDown',
  closeOnClickOutside = true,
}: PopoverContentProps) {
  const { isOpen, close } = usePopover();
  const contentRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!closeOnClickOutside) return;

    function handleClickOutside(event: MouseEvent) {
      if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
        // Check if click was on the trigger (parent's sibling)
        const parent = contentRef.current.parentElement;
        if (parent && !parent.contains(event.target as Node)) {
          close();
        } else {
          // Check if we clicked outside the entire popover container
          const container = parent?.parentElement;
          if (container && !container.contains(event.target as Node)) {
            close();
          }
        }
      }
    }

    if (isOpen) {
      // Delay to prevent immediate close on trigger click
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, close, closeOnClickOutside]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, close]);

  const alignmentClasses = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 -translate-x-1/2',
  };

  const animationConfig = popoverAnimations[animation];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={contentRef}
          initial={animationConfig.initial}
          animate={animationConfig.animate}
          exit={animationConfig.exit}
          transition={{ duration: 0.15 }}
          style={side === 'top' ? { bottom: '100%', marginBottom: sideOffset } : { marginTop: sideOffset }}
          className={`
            absolute z-50
            ${alignmentClasses[align]}
            ${className}
          `}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Pre-styled panel for common use cases
interface PopoverPanelProps {
  children: ReactNode;
  className?: string;
  width?: string;
  maxHeight?: string;
}

export function PopoverPanel({
  children,
  className = '',
  width = 'w-80',
  maxHeight = 'max-h-96',
}: PopoverPanelProps) {
  return (
    <div
      className={`
        ${width} ${maxHeight}
        bg-background-card rounded-xl shadow-lg border border-border overflow-hidden
        ${className}
      `}
    >
      {children}
    </div>
  );
}

// Header for popover panels
interface PopoverHeaderProps {
  children: ReactNode;
  className?: string;
}

export function PopoverHeader({ children, className = '' }: PopoverHeaderProps) {
  return (
    <div className={`px-4 py-3 border-b border-border ${className}`}>
      {children}
    </div>
  );
}

// Scrollable body for popover panels
interface PopoverBodyProps {
  children: ReactNode;
  className?: string;
  maxHeight?: string;
}

export function PopoverBody({ children, className = '', maxHeight = 'max-h-72' }: PopoverBodyProps) {
  return (
    <div className={`overflow-y-auto ${maxHeight} ${className}`}>
      {children}
    </div>
  );
}
